// src/context/GameContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import { Updater, useImmer } from 'use-immer'

export type GameGuides = Record<
  string,
  boolean | Record<string, boolean>
>

export type GamePasses = Record<string, boolean>

export type GameHints = Record<string, Record<string, boolean>>

type GameState = {
  confirms: Record<string, boolean>
  passes: GamePasses
  updatePasses: Updater<GamePasses>
  prevPathname: string
  hints: GameHints
  updateHints: Updater<GameHints>
  role: string
  updateRole: Updater<string>
  selectRole: boolean
  updateSelectRole: Updater<boolean>
  video: boolean
  updateVideo: Updater<boolean>
}

const initialState: GameState = {
  confirms: {},
  passes: {},
  updatePasses: () => {},
  prevPathname: '/',
  hints: {},
  updateHints: () => {},
  role: '',
  updateRole: () => {},
  selectRole: false,
  updateSelectRole: () => {},
  video: false,
  updateVideo: () => {},
}

const GameContext = createContext(initialState)

function GameContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const confirms = useRef<Record<string, boolean>>({})

  const [passes, updatePasses] = useImmer<GamePasses>({})

  const prevPathname = useRef<string>('/')
  const location = useLocation()
  useEffect(() => {
    // console.log(location.pathname)
    setTimeout(() => {
      prevPathname.current = location.pathname
    }, 200)
  }, [location])

  const [hints, updateHints] = useImmer<GameHints>({})
  const [role, updateRole] = useImmer<string>('')
  const [selectRole, updateSelectRole] = useImmer<boolean>(false)
  const [video, updateVideo] = useImmer<boolean>(false)

  const [value, setValue] = useLocalStorage<{
    passes: GamePasses
    hints: GameHints
    role: string
    selectRole: boolean
    video: boolean
  } | null>('loveproducer', null)
  const [init, setInit] = useState(true)
  useEffect(() => {
    if (value !== null) {
      // restore setting
      // console.log({ value })
      updatePasses(value?.passes ?? {})
      updateHints(value?.hints ?? {})
      updateRole(value?.role ?? '')
      updateSelectRole(value?.selectRole ?? false)
      updateVideo(value?.video ?? false)
    } else {
      // console.log('no initial game state')
    }
    setTimeout(() => setInit(false), 300)
  }, [
    setInit,
    updatePasses,
    updateHints,
    updateRole,
    updateSelectRole,
    updateVideo,
  ])

  useEffect(() => {
    if (init) return
    setValue({ passes, hints, role, selectRole, video })
  }, [init, passes, hints, role, setValue, selectRole, video])

  return (
    <GameContext.Provider
      value={{
        confirms: confirms.current,
        passes,
        updatePasses,
        prevPathname: prevPathname.current,
        hints,
        updateHints,
        role,
        updateRole,
        selectRole,
        updateSelectRole,
        video,
        updateVideo,
      }}
    >
      {children}
    </GameContext.Provider>
  )
}

export default GameContextProvider

export const useGameContext = () => useContext(GameContext)
